// El veredicto de UNA consulta al proveedor leído como tres filas fijas —Candado, Señal y
// Pendiente—, las mismas en los cuatro casos de proveedor × modo. El componente solo las pinta.
import { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import type { MdmRefreshOutcome, MdmRefreshReason } from '@/api/services/loan-credit/contract';
import {
    dayCountLabel,
    fleetStateLabel,
    operationCountLabel,
    readFleetState,
    readHeartbeat,
} from './logic';
import type { MdmFleetDevice, MdmRefreshReview } from './types';

export interface MdmRefreshLine {
    label: 'Candado' | 'Señal' | 'Pendiente';
    /** Lo que dice ahora. */
    value: string;
    /** Lo que decía antes; ausente cuando no cambió. */
    before?: string;
    /** La aclaración de la fila, cuando hace falta. */
    note?: string;
    tone: 'ok' | 'warn' | 'danger' | 'neutral';
}

/** Lo que la fila «Candado» dice en cada desenlace. Completo: sumar un valor rompe la compilación. */
const LOCK_BY_OUTCOME: Record<MdmRefreshOutcome, { value: string; tone: MdmRefreshLine['tone'] }> =
    {
        PROVIDER_SILENT: { value: 'El proveedor no contestó', tone: 'warn' },
        LOCK_CONFIRMED: { value: 'Bloqueado, confirmado por el proveedor', tone: 'ok' },
        UNLOCK_CONFIRMED: { value: 'Sin candado, confirmado por el proveedor', tone: 'ok' },
        LOCK_REFUTED: { value: 'El proveedor dice que no hay candado', tone: 'danger' },
        PROVIDER_LOCKED: { value: 'El proveedor lo tiene bloqueado', tone: 'danger' },
        NO_EVIDENCE: { value: 'Sin evidencia nueva', tone: 'neutral' },
    };

/** El motivo del `NO_EVIDENCE`, que es lo que cambia lo que el operador hace. */
const LOCK_NOTE_BY_REASON: Record<MdmRefreshReason, string> = {
    NOT_DUE: 'Todavía no se puede juzgar: el corte está dentro de su margen.',
    SILENT_SINCE_DUE: 'El equipo no se conectó desde el corte: está apagado o sin red.',
    NO_ENTRY_SINCE_DUE: 'El proveedor no registró actividad desde el corte.',
    UNKNOWN: 'El proveedor contestó algo que no sabemos leer: escálalo.',
};

/** El desbloqueo por pago dispara un aviso al cliente desde el backend; decirlo evita repetirlo a mano. */
const UNLOCK_NOTICE_NOTE = 'Le avisamos al cliente que su equipo quedó libre.';

/** Lo que la consulta NO hace: cerrar la operación en vuelo. Eso lo hace el sondeo automático. */
const PENDING_NOTE = 'Esta consulta no la cierra: la cierra el sondeo automático.';

/** Lo que dice la fila «Pendiente» cuando el equipo no tiene nada esperando al proveedor. */
const NO_PENDING_VALUE = 'Ninguna';

function lockNote(review: MdmRefreshReview, before: MdmFleetDevice): string | undefined {
    if (review.reason) return LOCK_NOTE_BY_REASON[review.reason];
    if (
        review.outcome === 'UNLOCK_CONFIRMED' &&
        before.lockReason === LoanDeviceLockReasonEnum.OVERDUE_T_PLUS_1
    ) {
        return UNLOCK_NOTICE_NOTE;
    }
    return undefined;
}

function lockLine(
    review: MdmRefreshReview,
    before: MdmFleetDevice,
    after: MdmFleetDevice,
    now: Date | string,
): MdmRefreshLine {
    const { value, tone } = LOCK_BY_OUTCOME[review.outcome];
    const wasState = fleetStateLabel(readFleetState(before, now));
    const isState = fleetStateLabel(readFleetState(after, now));
    const note = lockNote(review, before);
    return {
        label: 'Candado',
        value,
        tone,
        ...(wasState === isState ? {} : { before: wasState }),
        ...(note ? { note } : {}),
    };
}

function signalLine(
    before: MdmFleetDevice,
    after: MdmFleetDevice,
    now: Date | string,
): MdmRefreshLine {
    const wasSeen = readHeartbeat(before.lastSeenAt, now);
    const isSeen = readHeartbeat(after.lastSeenAt, now);
    return {
        label: 'Señal',
        value: isSeen.longLabel,
        tone: isSeen.severity,
        ...(wasSeen.longLabel === isSeen.longLabel ? {} : { before: wasSeen.longLabel }),
    };
}

function pendingLine(after: MdmFleetDevice): MdmRefreshLine {
    if (after.pendingOperations <= 0) {
        return { label: 'Pendiente', value: NO_PENDING_VALUE, tone: 'neutral' };
    }
    return {
        label: 'Pendiente',
        value: operationCountLabel(after.pendingOperations),
        tone: 'warn',
        note: PENDING_NOTE,
    };
}

/** Las tres filas del panel. Siempre tres, en este orden, en los cuatro casos. */
export function readRefreshLines(
    review: MdmRefreshReview,
    before: MdmFleetDevice,
    after: MdmFleetDevice,
    /** Instante de referencia; los tests lo fijan, la ficha usa el suyo. */
    now: Date | string = new Date(),
): [MdmRefreshLine, MdmRefreshLine, MdmRefreshLine] {
    return [
        lockLine(review, before, after, now),
        signalLine(before, after, now),
        pendingLine(after),
    ];
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** «hace un momento» / «hace 5 minutos» / «hace 2 horas»: qué tan fresca es la respuesta del proveedor. */
export function observedAgoLabel(observedAt: string, now: Date | string): string {
    const elapsed = new Date(now).getTime() - new Date(observedAt).getTime();
    if (!Number.isFinite(elapsed) || elapsed < MINUTE_MS) return 'hace un momento';
    if (elapsed < HOUR_MS) {
        const minutes = Math.floor(elapsed / MINUTE_MS);
        return `hace ${String(minutes)} ${minutes === 1 ? 'minuto' : 'minutos'}`;
    }
    if (elapsed < DAY_MS) {
        const hours = Math.floor(elapsed / HOUR_MS);
        return `hace ${String(hours)} ${hours === 1 ? 'hora' : 'horas'}`;
    }
    return `hace ${dayCountLabel(Math.floor(elapsed / DAY_MS))}`;
}
